// src/pages/GamePage/index.jsx
import React, { useEffect, useState } from "react";
import axios from "axios";
import { Gamepad2 } from "lucide-react";
import "./style.css";
import useDebounce from "../../components/hooks/useDebounce";
import { useNavigate } from "react-router-dom";

const API_BASE = process.env.REACT_APP_TWITCH_BASE_URL || "https://us-central1-tv-review-site.cloudfunctions.net";

const GamePage = () => {
  const [games, setGames] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const debouncedQ = useDebounce(q, 500);
  const [genres, setGenres] = useState([]);
  const [selectedGenres, setSelectedGenres] = useState([]);
  const [page, setPage] = useState(1);
  const [perPage] = useState(20);
  const [totalResults, setTotalResults] = useState(null);
  const [viewMode, setViewMode] = useState("grid");
  const navigate = useNavigate();

  useEffect(() => {
    // load genres once
    async function loadGenres() {
      try {
        const res = await axios.get(`${API_BASE}/api/genres`);
        setGenres(res.data || []);
      } catch (err) {
        console.error("Failed to load genres", err);
      }
    }
    loadGenres();
  }, []);

  useEffect(() => {
    // fetch games when debounced query, selectedGenres, or page changes
    async function fetchGames() {
      setLoading(true);
      try {
        const body = {
          q: debouncedQ,
          genres: selectedGenres,
          page,
          perPage,
          sort: "rating desc",
        };
        const res = await axios.post(`${API_BASE}/api/search`, body);
        const { results } = res.data;
        // map IGDB shapes to your card shape
        const mapped = (results || []).map((g) => ({
          id: g.id,
          title: g.name,
          rating: Math.round(g.rating || 0),
          image: g.cover?.url
            ? "https:" + g.cover.url.replace("t_thumb", "t_cover_big")
            : "https://via.placeholder.com/600x400?text=No+Image",
          excerpt: g.summary || "No description available.",
          platform: g.platforms?.[0]?.name || "Unknown",
          publishDate: g.first_release_date
            ? new Date(g.first_release_date * 1000).toLocaleDateString()
            : "-",
        }));

        setGames(mapped);
        // no total provided by IGDB; could be approximated by cache or left null
        setTotalResults(mapped.length);
      } catch (err) {
        console.error("Search error", err);
      } finally {
        setLoading(false);
      }
    }
    fetchGames();
  }, [debouncedQ, selectedGenres, page, perPage]);

  function toggleGenre(id) {
    setPage(1);
    setSelectedGenres((prev) =>
      prev.includes(id) ? prev.filter((g) => g !== id) : [...prev, id]
    );
  }

  return (
    <div className="games-page-container">
      <div className="games-page-content">
        <h1>Please note that this page is still in construction and may have incomplete features.</h1>
        <div className="games-header">
        
          <div className="games-header-icon">
            <Gamepad2 size={28} />
          </div>
          <div>
            <h1 className="games-title">Game Reviews</h1>
            <p className="games-subtitle">Search IGDB — live results</p>
          </div>
        </div>

        <div className="games-header-row" style={{ flexWrap: "wrap", gap: 12 }}>
          <div style={{ flex: 1, minWidth: 220 }}>
            <input
              aria-label="Search games"
              value={q}
              onChange={(e) => {
                setPage(1);
                setQ(e.target.value);
              }}
              placeholder="Search games (e.g. Elden Ring, Zelda)"
              style={{
                width: "100%",
                padding: "10px 12px",
                borderRadius: 8,
                border: "1px solid #ddd",
                fontSize: 16,
              }}
            />
          </div>

          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <div className="games-view-toggle">
              <button
                className={`games-toggle-btn${viewMode === "grid" ? " active" : ""}`}
                onClick={() => setViewMode("grid")}
              >
                Grid
              </button>
              <button
                className={`games-toggle-btn${viewMode === "list" ? " active" : ""}`}
                onClick={() => setViewMode("list")}
              >
                List
              </button>
            </div>
          </div>
        </div>

        <div style={{ margin: "12px 0" }}>
          <strong>Genres:</strong>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 8 }}>
            {genres.map((g) => (
              <button
                key={g.id}
                onClick={() => toggleGenre(g.id)}
                className={`games-toggle-btn ${selectedGenres.includes(g.id) ? "active" : ""}`}
                style={{ padding: "6px 10px", fontSize: 14 }}
              >
                {g.name}
              </button>
            ))}
          </div>
        </div>

        {loading ? (
          <div className="games-loading-container">
            <div className="games-loading-spinner"></div>
            <p>Loading games...</p>
          </div>
        ) : (
          <>
            {games.length === 0 ? (
              <div className="games-no-results">
                <Gamepad2 size={48} />
                <h3>No games found</h3>
                <p>Try a different search term or clear filters.</p>
              </div>
            ) : (
              <div className={viewMode === "grid" ? "games-grid" : "games-list"}>
                {games.map((game) => (
                  <div
                    key={game.id}
                    className={viewMode === "grid" ? "games-grid-card" : "games-list-card"}
                    style={{ cursor: "pointer" }}
                    onClick={() => navigate(`/games/${game.id}`)}
                  >
                    <img
                      src={game.image}
                      alt={game.title}
                      className="games-card-poster"
                    />
                    <div className="games-card-content">
                      <h3 className="games-card-title">{game.title}</h3>
                      <p className="games-card-date">{game.publishDate}</p>
                      <p className="games-card-excerpt">
                        {game.excerpt.length > 140 ? game.excerpt.slice(0, 140) + "…" : game.excerpt}
                      </p>
                      <div className="games-card-meta">
                        <span className="games-card-rating">Rating: {game.rating}/100</span>
                        <br />
                        <span className="games-card-platform">Platform: {game.platform}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Pagination simple */}
            <div className="games-pagination" style={{ marginTop: 24 }}>
              <button
                className="games-pagination-btn"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
              >
                Previous
              </button>
              <div className="games-pagination-info">Page {page}</div>
              <button
                className="games-pagination-btn"
                onClick={() => setPage((p) => p + 1)}
              >
                Next
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default GamePage;
